import { pick } from "lodash";

function _cleanTypenameRecursive<T>(value: T): T {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(_cleanTypenameRecursive) as T;

  const cleaned = {} as Record<string | symbol, unknown>;
  for (const key in value) {
    if (key !== "__typename") cleaned[key] = _cleanTypenameRecursive(value[key]);
  }
  for (const sym of Object.getOwnPropertySymbols(value)) {
    cleaned[sym] = _cleanTypenameRecursive((value as Record<symbol, unknown>)[sym]);
  }
  return cleaned as T;
}

/**
 * Takes the response from a GraphQL query and strips out any `__typename` props.
 * @param gqlResponse: Any JSON serialisable object or falsy value.
 * @returns Response cleaned of `__typename` fields. If falsy you just get that back.
 */
export function cleanTypename<T>(gqlResponse?: T): T | undefined {
  return gqlResponse && _cleanTypenameRecursive(gqlResponse);
}

/**
 * Cleans GraphQL typename from an object whilst preserving any function properties (which may have been merged in for example).
 *
 * @param object - The object to process
 * @returns The cleaned object with function properties preserved, or undefined if no object provided
 */
export function cleanTypenameRetainFunctionProps<T>(object?: T): T | undefined {
  if (!object) return;

  const functionProps = Object.entries(object)
    .filter(([, value]) => typeof value === "function")
    .map(([key]) => key);

  const cleanedObject = cleanTypename(object);

  return { ...cleanedObject, ...pick(object, functionProps) } as T;
}
